import hypertrackerHttpRequest, { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import { AxiosResponse } from 'axios';
import {
    HypertrackerLeaderboardDataResponse,
    HypertrackerLeaderboardItemsResponse,
    HypertrackerLeaderboardsBiggestPositionsResponse,
    HypertrackerLeaderboardsTopClaimedResponse,
    HyperTrackerMostFavoriteResponse,
    MeType,
    TelegramConnectionHT,
    VaultConnection,
    WalletConnection,
} from '@/state/hypertracker/hypertrackerTypes';
import {
    Alert,
    CreateAlertRequest,
    FavoriteAccount,
    FavoriteLimits,
    SSETokenResponse,
    FavoriteCoin,
    UnifiedSearchResponse,
} from '@/lib/types/hypertrackerTypes';

export const getWallet = (params: { walletAddress: string }) =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.WALLETS.ITEM(params.walletAddress));

export const getConnections = (): Promise<AxiosResponse<WalletConnection[]>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.MY_ACCOUNTS);

export const getMyVaults = (): Promise<AxiosResponse<VaultConnection[]>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.MY.VAULTS);

export const getConnectionsUpdated = (params: string): Promise<AxiosResponse<WalletConnection>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.MY.ACCOUNT_UPDATED(params));

export const updateConnectionLabel = (payload: {
    address: string;
    privateName: string | null;
}): Promise<AxiosResponse<any>> => hypertrackerHttpRequest.put(HT_API_ROUTES.MY.ACCOUNTS, payload);

export const addAccountToFavorites = (
    params: FavoriteAccount,
): Promise<AxiosResponse<FavoriteAccount>> =>
    hypertrackerHttpRequest.post(HT_API_ROUTES.FAVORITES.ACCOUNTS, params);

export const addPositionToFavorites = (params: {
    posId: string;
    featured: boolean;
}): Promise<
    AxiosResponse<{
        posId: string;
        featured: boolean;
    }>
> => hypertrackerHttpRequest.post(HT_API_ROUTES.FAVORITES.POSITIONS, params);

export const deleteAccountFromFavorites = (params: {
    address: string;
}): Promise<AxiosResponse<boolean>> =>
    hypertrackerHttpRequest.delete(HT_API_ROUTES.FAVORITES.ACCOUNTS, { data: params });

export const deletePositionFromFavorites = (params: {
    posId: string;
}): Promise<AxiosResponse<boolean>> =>
    hypertrackerHttpRequest.delete(HT_API_ROUTES.FAVORITES.POSITIONS, { data: params });

export const getFavoriteLimitsUpdated = (): Promise<AxiosResponse<FavoriteLimits>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.FAVORITES.LIMITS_UPDATED);
export const getGuestToken = (guestId: string): Promise<AxiosResponse<SSETokenResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.GUEST.TOKEN, { params: { guestId } });

// kept as an alias so existing SSE call sites don't need to change
export const getServerSentEventsToken = getGuestToken;

export const searchMembers = (params: {
    q: string;
    limit?: number;
}): Promise<AxiosResponse<UnifiedSearchResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.SEARCH.ROOT, { params });

export const searchGuest = (
    params: { q: string; limit?: number },
    guestToken: string,
): Promise<AxiosResponse<UnifiedSearchResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.GUEST.SEARCH, {
        params,
        headers: { Authorization: `Bearer ${guestToken}` },
    });

export const getLeaderboardPerpPnl = (
    params,
): Promise<AxiosResponse<HypertrackerLeaderboardDataResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.LEADERBOARDS.PERP_PNL, { params });

export const getLeaderboardBiggestPositions = (
    params,
): Promise<AxiosResponse<HypertrackerLeaderboardsBiggestPositionsResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.POSITIONS.ROOT_SLASH, { params });

export const getLeaderboardTopHypeHolders = (
    params,
): Promise<AxiosResponse<HypertrackerLeaderboardItemsResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.HYPE.HOLDERS_SLASH, { params });

export const getLeaderboardTopClaimed = (
    params,
): Promise<AxiosResponse<HypertrackerLeaderboardsTopClaimedResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.LEADERBOARDS.TOP_CLAIMED_SLASH, { params });

export const getLeaderboardRichList = (
    params,
): Promise<AxiosResponse<HypertrackerLeaderboardsTopClaimedResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.LEADERBOARDS.RICH_LIST_SLASH, { params });

export const getLeaderboardTopFavorite = (
    params,
): Promise<AxiosResponse<HyperTrackerMostFavoriteResponse>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.LEADERBOARDS.MOST_FAVORITED_SLASH, { params });

export const createAlert = (params: CreateAlertRequest): Promise<AxiosResponse<Alert>> =>
    hypertrackerHttpRequest.post(HT_API_ROUTES.ALERTS.ROOT_SLASH, params);

export const updateAlert = ({
    params,
    id,
}: {
    params: CreateAlertRequest;
    id: number;
}): Promise<AxiosResponse<Alert>> =>
    hypertrackerHttpRequest.put(HT_API_ROUTES.ALERTS.ITEM_SLASH(id), params);
export const deleteAlert = (id: number): Promise<AxiosResponse<boolean>> =>
    hypertrackerHttpRequest.delete(HT_API_ROUTES.ALERTS.ITEM_SLASH(id));

export const getMe = (): Promise<AxiosResponse<MeType>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.ME_SLASH);

export const getStatusTelegram = (): Promise<
    AxiosResponse<{
        connection: TelegramConnectionHT;
        status: string;
    }>
> => hypertrackerHttpRequest.get(HT_API_ROUTES.TELEGRAM_CONNECTION);

export const getLinkForConnectTelegram = (): Promise<AxiosResponse<string>> =>
    hypertrackerHttpRequest.post(HT_API_ROUTES.TELEGRAM_CONNECTION);

export const disconnectTelegram = (): Promise<AxiosResponse<any>> =>
    hypertrackerHttpRequest.delete(HT_API_ROUTES.TELEGRAM_CONNECTION);

export const getBasedTradeProgress = (): Promise<AxiosResponse<any>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.REFERRALS.BASED_TRADE_PROGRESS_SLASH);

export const getHlTradeProgress = (): Promise<AxiosResponse<any>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.REFERRALS.HL_TRADE_PROGRESS_SLASH);

export const getFavoriteCoins = (): Promise<AxiosResponse<FavoriteCoin[]>> =>
    hypertrackerHttpRequest.get(HT_API_ROUTES.FAVORITES.COINS);

export const addCoinToFavorites = (params: FavoriteCoin): Promise<AxiosResponse<FavoriteCoin>> =>
    hypertrackerHttpRequest.post(HT_API_ROUTES.FAVORITES.COINS, params);

export const deleteCoinFromFavorites = (params: {
    coin: string;
    type: string;
}): Promise<AxiosResponse<boolean>> =>
    hypertrackerHttpRequest.delete(HT_API_ROUTES.FAVORITES.COINS, { data: params });
